<template>
  <div>
    <h5 class="headline-title" :class="titleClassModifier">
      <link-handler :href="headline.href" v-html="getTitle(headline)" @click.native="headlineClick(headline)" />
      <template v-if="showCommentsCount && !comments.disableComments && getItemFromObject(headline, 'statistics.comments') > 0">
        <link-handler :href="commentsPath" class="headline-title__comments-count">
          <span class="comment-count" v-html="commentCount" />
        </link-handler>
      </template>
      <BaseButton
        icon="social-facebook-circle"
        icon-position="left"
        size="auto"
        @click.native="popupWindow(facebookUrl)"
        v-if="showFacebookShareCount && facebookShareCount"
        class="headline-title__share"
        >{{ facebookShareCount }}</BaseButton
      >
    </h5>
    <span
      v-if="titleCommentEnabled && getItemFromObject(headline, 'content.subtitle.text')"
      :class="['headline-title__comment', hasTagId(96596571) ? 'headline-title__comment--saugu' : '']"
      >{{ getItemFromObject(headline, 'content.subtitle.text') }}</span
    >
    <span v-if="showViewsCount && views" class="headline-title__views">
      <i class="fa-light fa-eye" />
      {{ views }}
    </span>
    <span v-if="showVideoDuration && videoDuration" class="headline-title__video-duration">
      <i class="fa-light fa-clock" />
      {{ videoDuration }}
    </span>
    <client-only>
      <div v-if="definedLabels.length > 0" class="headline-title__labels">
        <div v-for="(definedLabel, index) in definedLabels" :key="`title-label-${index}`" class="headline-title__labels-item">
          <span v-if="definedLabel.text" :style="definedLabel.textColor ? { color: definedLabel.textColor } : undefined">{{ definedLabel.text }}</span>
          <img v-if="definedLabel.image" :src="definedLabel.image" :style="definedLabel.imageWidth ? { width: definedLabel.imageWidth } : undefined" />
          <div v-if="definedLabel.html" v-html="definedLabel.html"></div>
        </div>
      </div>
    </client-only>
  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import { HeadlineWithRelatedArticles } from '@headlines/types/headline';
import BaseButton from '@core/components/UI/BaseButton.vue';
import LinkHandler from '@core/components/helpers/LinkHandler.vue';
import { gridConfig } from '@core/config/grid.config';
import { getRootConfig, convertArrayToObject } from '@core/utils/helpers';
import getItemFromObject from 'lodash/get';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import hasNapoComments from '@root/modules/article/utils/hasNapoComments';
import { getDefinedLabels } from '@headlines/utils/getDefinedLabels';
import { Label } from '@root/modules/channel/types/channel';
import { stripTags } from '@root/common/filters/htmlFormater';

type ShareData = {
  url: string;
  title: string;
  text: string;
};

interface ShNavigator extends Navigator {
  share: (data?: ShareData) => Promise<void>;
}

interface ShWindow extends Window {
  [index: string]: any;
  navigator: ShNavigator;
}

declare let window: ShWindow;

interface Props {
  headline: HeadlineWithRelatedArticles;
  titleSize: string;
  mobileTitleSize: string;
  titleCommentEnabled: boolean;
  showFacebookShareCount: boolean;
  showCommentsCount: boolean;
  showViewsCount: boolean;
  showVideoDuration: boolean;
}

interface Computed {
  titleClassModifier: Record<string, any>;
  facebookShareCount: number | null;
  commentCount: string | number;
  commentsPath: string;
  facebookUrl: string;
  isMobile: boolean;
  views: number | null;
  videoDuration: string | boolean;
  definedLabels: Label[];
}

interface Methods {
  popupWindow: (url: string) => void;
  getItemFromObject: typeof getItemFromObject;
  headlineClick: (headline: HeadlineWithRelatedArticles) => void;
  hasTagId: (tagId: number) => boolean;
  getTitle: (headline: HeadlineWithRelatedArticles, strip: boolean) => string;
}

interface Data {
  popup: {
    width: number;
    height: number;
    name: string;
  };
  shareUrl: string;
}

const sizeList = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h6',
  h4: 'h6',
  h5: 'h6',
  h6: 'h6',
} as Record<string, string>;

dayjs.locale('lt');
dayjs.extend(duration);

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    LinkHandler,
    BaseButton,
  },
  data() {
    return {
      popup: {
        width: 500,
        height: 400,
        name: 'share_dialog',
      },
      shareUrl: `${getRootConfig('meta')?.base?.baseUrl}/a/${this.headline.id}`,
      comments: convertArrayToObject<{
        disableComments: boolean;
        onlyRegisteredUsersAllowedToComment: boolean;
      }>(this.headline?.comments, { disableComments: false, onlyRegisteredUsersAllowedToComment: false }),
    };
  },
  props: {
    /**
     * @values  {
     *  title: string,
     *  title_short: string,
     *  title_comment: string,
     *  comments: object,
     *  shares: array,
     *  href: string,
     * }
     */
    headline: {
      type: Object,
      required: true,
    },
    /**
     * @values 'h1', 'h2', 'h3', 'h4, 'h5, 'h6'
     */
    titleSize: {
      type: String,
      default: 'h2',
      required: false,
    },
    mobileTitleSize: {
      type: String,
      default: '',
      required: false,
    },
    titleCommentEnabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    showFacebookShareCount: {
      type: Boolean,
      default: true,
      required: false,
    },
    showCommentsCount: {
      type: Boolean,
      default: true,
      required: false,
    },
    showViewsCount: {
      type: Boolean,
      default: false,
      required: false,
    },
    showVideoDuration: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  computed: {
    titleClassModifier() {
      const { titleSize } = this;
      let mobileTitleSize = this.mobileTitleSize;

      if (!mobileTitleSize && titleSize in sizeList) {
        mobileTitleSize = sizeList[titleSize];
      }

      return {
        [`headline-title--size-${titleSize}`]: titleSize,
        [`headline-title--size-sm-${mobileTitleSize}`]: mobileTitleSize,
      };
    },
    facebookShareCount() {
      const shares = this.getItemFromObject(this.headline, 'statistics.shares');
      // FIXME: Should be taken from config
      // but is workarounded during migration
      // to git submodules
      return shares >= 20 ? shares : null;
    },
    commentCount() {
      const commentsNoBrackets = getRootConfig('settings')?.commentsNoBrackets;
      const commentNumber = getItemFromObject(this.headline, 'statistics.comments');
      const commentCount = commentsNoBrackets ? commentNumber : `(${commentNumber})`;

      return commentCount;
    },
    commentsPath() {
      return `${this.headline.href}/${hasNapoComments(this.headline) ? 'napo' : this.$t('portal_core.comments')}`;
    },
    facebookUrl() {
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURI(this.shareUrl)}&t=${encodeURI(this.getTitle(this.headline, true))}`;
    },
    isMobile() {
      return screen.width <= gridConfig.breakpoints.lg;
    },
    views() {
      if (!this.showViewsCount) {
        return false;
      }

      return this.$props.headline.statistics.views;
    },
    videoDuration() {
      if (!this.showVideoDuration) {
        return false;
      }

      if (!this.$props.headline.relatedEntities.media.items.length) {
        return false;
      }

      if (!this.$props.headline.relatedEntities.media.items[0].videoData) {
        return false;
      }

      const duration = this.$props.headline.relatedEntities.media.items[0].videoData.videoDuration;

      return duration ? dayjs.duration(duration, 'seconds').format('mm:ss') : false;
    },
    definedLabels() {
      const definedLabels = getDefinedLabels(this.headline, 'title');
      return definedLabels;
    },
  },
  methods: {
    popupWindow(url) {
      const params = `width=${this.popup.width},height=${this.popup.height},directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no`;
      const lead = this.getItemFromObject(this.headline, 'content.lead.text') || '';
      const title = this.getTitle(this.headline, true);

      if (window.navigator.share && this.isMobile) {
        window.navigator.share({
          title,
          url: this.shareUrl,
          text: lead,
        });
      } else if (window.AndroidWebAppMobileAPI && window.AndroidWebAppMobileAPI.sharePage) {
        window.AndroidWebAppMobileAPI.sharePage(this.shareUrl, title, lead);
      } else {
        window.open(url, this.popup.name, params);
      }
    },
    getItemFromObject,
    headlineClick(headline: HeadlineWithRelatedArticles) {
      this.$instanceConnector('headlines') && this.$instanceConnector('headlines').headlineClick({ headline });
    },
    hasTagId(tagId) {
      return !!this.headline.tags?.items?.find((tag) => tag.id === tagId);
    },
    getTitle(headline, strip = false) {
      const title = headline.content.title?.html
        ? headline.content.title?.html.replace(/(<\/?h1>)/gi, '')
        : headline.content.title?.text
          ? headline.content.title?.text
          : '';

      return strip && typeof stripTags === 'function' ? stripTags(title) : title;
    },
  },
});
</script>
<style lang="scss">
.headline-author {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 10px 0;
  padding: 0;
  color: #000;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.7;

  i {
    svg {
      width: 15px;
    }
  }

  &__separator {
    content: '|';
    display: block;
    margin: 0 4px;
    color: #ddd;
    font-size: 7px;
  }
}

.headline-title {
  margin-top: 15px;
  font-family: get-font-family('primary');
  font-weight: get-heading-settings('h1-lg-font-weight');
  line-height: get-heading-settings('h1-lg-line-height');
  word-break: break-word;
  color: get-color('text-1');
  transition: color $color-transition-time;

  &:hover {
    color: get-color('text-1-hover');
  }

  @include media-breakpoint-down(sm) {
    font-weight: get-heading-settings('h1-sm-font-weight');
    line-height: get-heading-settings('h1-sm-line-height');
  }

  // Generate title size from .headline__title--size-h1 to .headline__title--size-h6
  $heading-size-value: ('h1', 'h2', 'h3', 'h4', 'h5', 'h6');
  @each $key, $value in $heading-size-value {
    &--size-#{$key} {
      font-size: get-heading-settings('#{$key}-lg-font-size');
    }
  }

  @each $key, $value in $heading-size-value {
    &--size-sm-#{$key} {
      @include media-breakpoint-down(sm) {
        font-size: get-heading-settings('#{$key}-sm-font-size');
      }
    }
  }

  &__link {
    display: inline;
  }

  &__comments-count {
    display: inline;
  }

  &__comment {
    display: block;
    position: relative;
    font-size: 13px;
    font-weight: get-font-weight('regular');
    font-family: get-font-family('system');
    color: get-color('link-2');
    padding-left: 20px;

    &:before {
      content: '';
      position: absolute;
      top: 10px;
      left: 0;
      background: get-color('link-2');
      width: 12px;
      height: 1px;
    }
    &--saugu {
      color: #02b580;
      &:before {
        background: #02b580;
      }
    }
  }

  &__share {
    font-size: 12px;
    color: get-color('grey-1');
    font-weight: get-font-weight('medium');
    vertical-align: middle;
    white-space: nowrap;

    i {
      display: inline-block;
      vertical-align: middle;
      margin: -3px 3px 0 3px !important;
    }

    span {
      vertical-align: middle;
    }
  }

  &__views {
    margin-right: 10px;
    color: var(--color-grey-1);
    font-size: 12px;
    font-weight: 500;
    line-height: 15px;

    i {
      margin-right: 2px;
    }
  }

  &__video-duration {
    margin-right: 10px;
    color: var(--color-grey-1);
    font-size: 12px;
    font-weight: 500;
    line-height: 15px;

    i {
      margin-right: 2px;
    }
  }
  &__labels {
    &-item {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 7px;
      span {
        color: #888;
        font-size: 12px;
      }
      img {
        max-width: 100%;
      }
    }
  }
}
</style>
